/**
 * Login-id helpers for pickers and "is this me" comparisons.
 *
 * `profiles.id` is the profile PK; `profiles.user_id` is the LOGIN id
 * (= auth.uid()). They are never equal (0 of 26 profiles on prod, 2026-10-07).
 * Every column these helpers feed — training_records.user_id,
 * training_session_attendees.user_id, group_members.user_id,
 * messages.sender_id / recipient_id — holds the login id, and RLS compares it
 * with auth.uid(). Offering `profiles.id` instead produced writes that either
 * failed the auth.users FK (training, groups) or that no recipient could read
 * (direct messages, which have no FK). HDQ-WEB-ID-1 / HDQ-WEB-ID-2.
 *
 * Interim: once PR #1472 (src/lib/people.ts, PersonPicker) merges, the pickers
 * that use these helpers should move to PersonPicker.
 */

/**
 * Canonical form of a uuid for comparison. Postgres serialises uuids lowercase,
 * but iOS writes `UUID.uuidString`, which is uppercase — so a raw `===` between
 * an id iOS wrote and one the web holds can silently miss. Returns null for an
 * empty value so "no id" never compares equal to "no id".
 */
export function normaliseLoginId(id: string | null | undefined): string | null {
  if (typeof id !== "string") return null;
  const trimmed = id.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

/** True when both are present and name the same login, case-insensitively. */
export function sameLoginId(a: string | null | undefined, b: string | null | undefined): boolean {
  const na = normaliseLoginId(a);
  return na !== null && na === normaliseLoginId(b);
}

export interface ProfileWithLoginId {
  user_id: string | null;
}

/**
 * The people a picker may offer, keyed by login id: drops profiles with no
 * sign-in account (nothing to address — a write with their id would fail the
 * FK or be unreadable), optionally drops the signed-in user, and normalises
 * `user_id` to lowercase so it can be used directly as the picker value.
 */
export function pickableByLoginId<T extends ProfileWithLoginId>(
  profiles: readonly T[] | null | undefined,
  excludeLoginId?: string | null,
): Array<T & { user_id: string }> {
  const exclude = normaliseLoginId(excludeLoginId);
  const out: Array<T & { user_id: string }> = [];
  for (const p of profiles ?? []) {
    const loginId = normaliseLoginId(p.user_id);
    if (loginId === null || loginId === exclude) continue;
    out.push({ ...p, user_id: loginId });
  }
  return out;
}
